import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { getAppBasename, useAppRouter } from '../hooks/useAppRouter';
import { redirectToSignIn } from '../services/provider/authService';
import { getPublicRuntimeConfig } from '../libs/init/getPublicRuntimeConfig';
import { shouldRouteClientSide } from '../utils/shouldRouteClientSide';
import {
  prepareSideloadFromAsPath,
  redirectAfterSideloadPrepare,
} from '../features/extensions/sideloading';

enum State {
  Initial = 'Initial',
  RoutingInProgress = 'RoutingInProgress',
  NotFound = 'NotFound',
}

type BuildManifestRewrite = {
  destination?: string;
};

type BuildManifest = {
  __rewrites?: {
    beforeFiles?: BuildManifestRewrite[];
  };
};

/**
 * Strips the dev-only entry rewrite (layer 1 in `buildNextRewrites`) from the client manifest.
 *
 * `next.config.mjs` sends non-asset URLs to this page on first load (prod-like nginx entry).
 * That catch-all is also copied into `window.__BUILD_MANIFEST.__rewrites`, so without removing
 * it, `router.push(asPath)` would loop here. Slug rewrites (layer 2) stay so pretty URLs
 * resolve to exported pages — see `buildNextRewrites` in `next.config.mjs`.
 */
const removeClientSideStaticSiteRouterRewrite = (
  staticSiteRouterDestination: string,
): void => {
  const buildManifest = (
    window as Window & { __BUILD_MANIFEST?: BuildManifest }
  ).__BUILD_MANIFEST;

  const beforeFiles = buildManifest?.__rewrites?.beforeFiles;

  if (!beforeFiles) {
    return;
  }

  buildManifest.__rewrites!.beforeFiles = beforeFiles.filter(
    (rewrite) => rewrite.destination !== staticSiteRouterDestination,
  );
};

/*
 * When deployed via static site generation (SSG) the edge router directs the intial
 * page request to this router page. From here the Next.js page router takes over to find
 * the required page.
 * This approach allows us to make full use of the Next.js page router including
 * dyanmic path segmenents, catch all routes, etc.
 */
export function StaticSiteRouter(): ReactElement {
  const router = useAppRouter();

  const [state, setState] = useState(State.Initial);

  const requiresClientSideRouting = useMemo(() => {
    const runsInBrowser = typeof window !== 'undefined';
    const clientSide = shouldRouteClientSide(router.asPath, router.pathname);
    return runsInBrowser && clientSide;
  }, [router.asPath, router.pathname]);

  useEffect(() => {
    if (!requiresClientSideRouting || state !== State.Initial) {
      return;
    }

    setState(State.RoutingInProgress);

    (async () => {
      try {
        removeClientSideStaticSiteRouterRewrite(
          `${getAppBasename()}${router.pathname}`,
        );

        const sideloadPrepare = prepareSideloadFromAsPath(router.asPath);

        if (sideloadPrepare) {
          redirectAfterSideloadPrepare(sideloadPrepare);
          return;
        }

        // Browser path often matches asPath while this page is still mounted; a hash
        // in asPath can make router.push a no-op (see redirectAfterSideloadPrepare).
        await router.push(router.asPath);
      } catch {
        setState(State.NotFound);
      }
    })();
  }, [requiresClientSideRouting, state, setState, router]);

  // The router didn't recognise the route
  useEffect(() => {
    if (state === State.NotFound) {
      const { paths } = getPublicRuntimeConfig();
      redirectToSignIn(`/v2${paths.dashboard}`);
    }
  }, [state]);

  // Routing is still in progress
  return <></>;
}

export default StaticSiteRouter;
